import {
  FaTachometerAlt, FaUserTie, FaUsers, FaHome, FaCalendarAlt, FaClipboardList,
  FaTools, FaFileInvoiceDollar, FaChartBar, FaEnvelopeOpenText, FaBullhorn,
  FaChartLine, FaHistory, FaWrench, FaCog, FaPlus, FaMoneyCheckAlt, FaUserShield,
} from 'react-icons/fa';
import { terminology } from './terminology';

// Single source of truth for the admin's left-hand navigation. The sidebar renders these groups,
// and the dashboard's quick links are chosen from the same set — a quick link is stored as its path
// alone, so both the label and the icon must be resolvable from here.

export const NAV_GROUPS = [
  { label: null, items: [
    { name: 'Dashboard', path: '/', icon: FaTachometerAlt, exact: true },
  ]},
  { label: 'People', items: [
    { name: 'Franchisees',                 path: '/franchisees',  icon: FaUserTie },
    { name: terminology.client.plural,     path: '/clients',      icon: FaUsers },   // "Suppliers"
    { name: terminology.household.plural,  path: '/households',   icon: FaHome },    // "Clients"
  ]},
  { label: 'Work', items: [
    { name: 'Job Scheduler', path: '/job-scheduler', icon: FaCalendarAlt },
    { name: 'Jobs List',     path: '/jobs-list',     icon: FaClipboardList },
    { name: 'Services',      path: '/services',      icon: FaTools },
  ]},
  { label: 'Finance', items: [
    { name: 'Invoices',          path: '/invoices',          icon: FaFileInvoiceDollar },
    { name: 'Chart of Accounts', path: '/chart-of-accounts', icon: FaChartBar },
    { name: 'Supplier Payments', path: '/supplier-payments', icon: FaMoneyCheckAlt },
  ]},
  { label: 'Comms', items: [
    { name: 'Email Monitor', path: '/email-monitor', icon: FaEnvelopeOpenText, feature: 'inboxMonitor' },
    { name: 'Messaging',     path: '/messaging',     icon: FaBullhorn },
  ]},
  { label: 'Admin', items: [
    { name: 'Reports',      path: '/reports',      icon: FaChartLine },
    { name: 'Activity Log', path: '/activity-log', icon: FaHistory },
    { name: 'Utilities',    path: '/utilities',    icon: FaWrench },
    { name: 'Admin Users',  path: '/admin-users',  icon: FaUserShield, superAdmin: true },
    { name: 'Settings',     path: '/settings',     icon: FaCog },
  ]},
];

// Destinations that earn a quick link without earning a sidebar row of their own. Nothing in the
// sidebar can add these back once removed, which is what the quick links' "Reset" button is for.
const EXTRA_QUICK_LINKS = [
  { name: 'New Job', path: '/job-scheduler/add', icon: FaPlus, accent: true },
];

// The dashboard opens with these for an admin who has never customised anything.
export const DEFAULT_QUICK_LINKS = ['/job-scheduler/add', '/jobs-list', '/invoices', '/reports'];

// Mirrors MAX_QUICK_LINKS in the API's userPreferenceService — the server truncates silently, so
// the UI has to stop offering more before it gets there.
export const MAX_QUICK_LINKS = 12;

// Everything in the sidebar except the dashboard itself — a quick link back to the page you are
// already on is not a shortcut.
const CATALOG = new Map(
  [...NAV_GROUPS.flatMap((group) => group.items).filter((item) => item.path !== '/'), ...EXTRA_QUICK_LINKS]
    .map((item) => [item.path, item])
);

/**
 * Whether a nav entry is available to this admin. Feature-flagged pages stay hidden until their
 * flag is on, and the quick links honour the same rule so a pinned page cannot outlive its flag.
 * Super-admin-only pages additionally require the config to say this caller is the super admin.
 */
export const isNavItemVisible = (item, config) => {
  if (item.superAdmin && config?.isSuperAdmin !== true) return false;
  return item.feature !== 'inboxMonitor' || config?.inboxMonitorEnabled === true;
};

/** The nav entry behind a stored path, or null once a page has been renamed or removed. */
export const navItemFor = (path) => CATALOG.get(path) || null;

/**
 * Resolves stored paths to renderable entries, dropping any this build no longer knows about and
 * any whose feature flag is currently off.
 */
export const resolveQuickLinks = (paths = [], config = null) =>
  paths.map(navItemFor).filter((item) => item && isNavItemVisible(item, config));

/** Quick links are only offered for pages the admin can actually reach. */
export const isQuickLinkable = (path) => CATALOG.has(path);
